// Vision Deck Editor Constants and Configuration

export const API = process.env.REACT_APP_BACKEND_URL || '';

// Template styles
export const TEMPLATE_STYLES = {
  'modern-executive': {
    name: 'Modern Executive',
    background: '#FFFEF5',
    text: '#1A1A1A',
    accent: '#B89B5E',
    secondaryBg: '#F5F5F0',
    muted: '#6B6B6B'
  },
  'contemporary-hospitality': {
    name: 'Contemporary Hospitality',
    background: '#FDF8F3',
    text: '#3D3D3D',
    accent: '#8B7355',
    secondaryBg: '#F0E6D8',
    muted: '#7D7D7D'
  },
  'corporate-prestige': {
    name: 'Corporate Prestige',
    background: '#1A1A1A',
    text: '#FFFFFF',
    accent: '#C9A962',
    secondaryBg: '#2D2D2D',
    muted: '#9A9A9A'
  }
};

// Block types configuration
export const BLOCK_TYPES = [
  { type: 'text', label: 'Text', icon: 'Type', description: 'Headlines, paragraphs, quotes' },
  { type: 'image', label: 'Image', icon: 'Image', description: 'Photos, graphics, artwork' },
  { type: 'video', label: 'Video', icon: 'Film', description: 'Video content with thumbnails' },
  { type: 'mobile-video', label: 'Mobile Video', icon: 'Play', description: 'Vertical 9:16 mobile video' },
  { type: 'logo', label: 'Logo', icon: 'Square', description: 'Brand marks and logos' },
  { type: 'divider', label: 'Divider', icon: 'Minus', description: 'Visual section breaks' },
  { type: 'cta', label: 'Call to Action', icon: 'MousePointer2', description: 'Buttons and links' },
  { type: 'service-adjuster', label: 'Service Adjuster', icon: 'SlidersHorizontal', description: 'Interactive quantity controls' },
  // Phase 7.1 — premium content blocks
  { type: 'testimonial', label: 'Testimonial', icon: 'MessageSquare', description: 'Customer quotes and social proof' },
  { type: 'stats', label: 'Stats & Metrics', icon: 'TrendingUp', description: 'Key numbers and impact metrics' },
  { type: 'contact-card', label: 'Your Contact', icon: 'UserCircle', description: 'Rep profile and contact details' },
  { type: 'comparison', label: 'Before & After', icon: 'Columns', description: 'Side-by-side comparison or competitive view' },
  // Phase 7.2 — premium content blocks (urgency, pricing, planning, FAQ)
  { type: 'countdown', label: 'Proposal Deadline', icon: 'Timer', description: 'Urgency timer for time-sensitive offers' },
  { type: 'pricing', label: 'Pricing Options', icon: 'DollarSign', description: 'Plan tiers and pricing comparison' },
  { type: 'timeline', label: 'Project Timeline', icon: 'GitCommit', description: 'Rollout plan and key milestones' },
  { type: 'faq', label: 'FAQ', icon: 'HelpCircle', description: 'Collapsible questions and answers' },
  // Phase 7.3 — premium content blocks (interactive)
  { type: 'roi-calculator', label: 'ROI Calculator', icon: 'Calculator', description: 'Interactive calculator prospects complete themselves' },
  { type: 'booking', label: 'Book a Meeting', icon: 'CalendarCheck', description: 'Embedded calendar booking link' },
  { type: 'lead-form', label: 'Request Form', icon: 'ClipboardList', description: 'Embedded form prospects submit directly' },
  { type: 'video-testimonial', label: 'Video Testimonial', icon: 'PlayCircle', description: 'Customer video with inline playback' },
  // Phase 3.3 — universal framework block
  { type: 'framework-matrix', label: 'Framework Matrix', icon: 'Grid3x3', description: 'Position items on a custom 2-axis matrix' },
  // Phase 3.6A — interactive whiteboard
  { type: 'whiteboard', label: 'Whiteboard', icon: 'PenTool', description: 'Draw shapes, arrows and labels on an editable canvas' },
];

// CTA Button presets
export const CTA_PRESETS = [
  { label: 'Learn More', value: 'Learn More' },
  { label: 'View Vision', value: 'View Vision' },
  { label: 'Explore Collection', value: 'Explore Collection' },
  { label: 'Request Consultation', value: 'Request Consultation' },
  { label: 'Book Presentation', value: 'Book Presentation' },
  { label: 'Get Started', value: 'Get Started' },
  { label: 'Contact Us', value: 'Contact Us' },
  { label: 'Schedule Demo', value: 'Schedule Demo' },
];

// Font options
export const FONT_OPTIONS = [
  { value: 'font-sans', label: 'Sans Serif' },
  { value: 'font-serif', label: 'Serif' },
  { value: 'font-mono', label: 'Monospace' },
];

// Font size options  
export const FONT_SIZES = [
  { value: 'text-sm', label: 'Small' },
  { value: 'text-base', label: 'Normal' },
  { value: 'text-lg', label: 'Large' },
  { value: 'text-xl', label: 'XL' },
  { value: 'text-2xl', label: '2XL' },
  { value: 'text-3xl', label: '3XL' },
  { value: 'text-4xl', label: '4XL' },
];

// Default block content
export const getDefaultBlockContent = (type) => {
  switch (type) {
    case 'text':
      return {
        content: '',
        font: 'font-sans',
        size: 'text-base',
        bold: false,
        italic: false,
        underline: false,
        align: 'left',
        color: null
      };
    case 'image':
      return {
        url: '',
        alt: '',
        caption: '',
        frame: { borderWidth: 0, borderColor: '#B89B5E', borderRadius: 8, shadow: true }
      };
    case 'video':
      return {
        url: '',
        thumbnailUrl: '',
        thumbnailType: 'custom',
        autoplay: false,
        loop: false,
        muted: true,
        frame: { borderWidth: 0, borderColor: '#B89B5E', borderRadius: 8, shadow: true }
      };
    case 'mobile-video':
      return {
        url: '',
        thumbnailUrl: '',
        thumbnailType: 'custom',
        autoplay: false,
        loop: false,
        muted: true,
        aspectRatio: '9:16',
        frame: { borderWidth: 0, borderColor: '#B89B5E', borderRadius: 16, shadow: true }
      };
    case 'logo':
      return { url: '', size: 'medium', align: 'center' };
    case 'divider':
      return { style: 'solid', color: '#E5E5E5', thickness: 1, width: '60%' };
    case 'cta':
      return { text: 'Learn More', url: '', style: 'primary', align: 'center', linkType: 'external', targetPageId: null };
    case 'service-adjuster':
      return {
        serviceName: '',
        category: '',
        description: '',
        currentQuantity: 0,
        adjustableQuantity: 0,
        incrementMode: 'single', // single, custom, 10, 20, 50, 100
        customStep: 1,
        minQuantity: 0,
        maxQuantity: null,
        unitLabel: 'units',
        internalNote: '',
        allowProspectNote: true
      };
    case 'testimonial':
      return {
        quote: 'This transformed how our team closes deals.',
        attribution_name: 'Jane Smith',
        attribution_title: 'VP of Sales',
        attribution_company: 'Acme Corp',
        photo_url: '',
        logo_url: '',
      };
    case 'stats':
      return {
        headline: 'The Impact',
        stats: [
          { value: '30%', label: 'Faster Proposals' },
          { value: '18%', label: 'Higher Win Rates' },
          { value: '90', label: 'Days to ROI' },
        ],
      };
    case 'contact-card':
      return {
        name: '',
        title: '',
        email: '',
        phone: '',
        bio: '',
        photo_url: '',
        show_email: true,
        show_phone: true,
        show_bio: true,
      };
    case 'comparison':
      return {
        headline: 'The Difference',
        mode: 'before-after', // 'before-after' | 'us-vs-them'
        left_label: 'Without Us',
        right_label: 'With Us',
        rows: [
          { left: 'Manual research takes hours', right: 'AI surfaces opportunities instantly' },
          { left: 'Inconsistent proposals', right: 'Branded decks in minutes' },
          { left: 'Deals stall without clear next steps', right: 'AI recommends next best action' },
        ],
      };
    case 'countdown':
      return {
        headline: 'This offer expires in',
        expiry_date: null, // ISO 8601 string e.g. '2026-03-15T17:00:00.000Z'
        expired_message: 'This offer has expired. Contact us to discuss next steps.',
        show_days: true,
        show_hours: true,
        show_minutes: true,
      };
    case 'pricing':
      return {
        headline: 'Investment Options',
        currency_symbol: '$',
        tiers: [
          {
            name: 'Starter',
            price: '',
            period: 'per month',
            description: '',
            features: ['Feature one', 'Feature two', 'Feature three'],
            is_recommended: false,
            cta_text: 'Get Started',
            cta_url: '',
          },
        ],
      };
    case 'timeline':
      return {
        headline: 'Your Rollout Plan',
        items: [
          { period: 'Week 1', title: 'Discovery & Setup', description: 'Align on goals, import data, configure your workspace.' },
          { period: 'Month 1', title: 'Launch & Activate', description: 'Go live with first signal sweep and initial opportunities.' },
          { period: 'Month 3', title: 'Scale & Optimize', description: 'Review results, tune scoring, expand to additional territories.' },
        ],
      };
    case 'faq':
      return {
        headline: 'Common Questions',
        items: [
          { question: 'How quickly can we get started?', answer: 'Most clients are up and running within 5 business days.' },
          { question: 'What does implementation look like?', answer: 'We handle the full setup. You just need one 30-minute kickoff call.' },
          { question: 'What if it is not the right fit?', answer: 'No long-term contracts. Cancel any time with 30 days notice.' },
        ],
      };
    case 'roi-calculator':
      return {
        headline: 'Calculate Your ROI',
        subheadline: 'See what this means for your business',
        inputs: [
          { id: 'team_size', label: 'Number of Sales Reps', default_value: 5, min: 1, max: 500, prefix: '', suffix: ' reps' },
          { id: 'avg_deal', label: 'Average Deal Size', default_value: 50000, min: 1000, max: 10000000, prefix: '$', suffix: '' },
          { id: 'close_rate', label: 'Current Close Rate', default_value: 20, min: 1, max: 100, prefix: '', suffix: '%' },
        ],
        outputs: [
          { id: 'additional_revenue', label: 'Additional Annual Revenue', formula: 'team_size * avg_deal * (close_rate / 100) * 0.18', prefix: '$', suffix: '', highlight: true },
          { id: 'time_saved', label: 'Hours Saved Per Rep Monthly', formula: 'team_size * 8', prefix: '', suffix: ' hours', highlight: false },
        ],
        cta_text: 'Get Your Custom Plan',
        cta_url: '',
      };
    case 'booking':
      return {
        headline: 'Ready to Get Started?',
        subheadline: 'Book a 20-minute call and we will build your custom plan within 48 hours.',
        booking_url: '',
        button_text: 'Schedule Your Call',
        provider: 'calendly', // auto-derived; kept for storage symmetry
        show_inline: false,
      };
    case 'lead-form':
      return {
        headline: 'Ready to Move Forward?',
        subheadline: 'Fill in your details and we will be in touch within one business day.',
        fields: [
          { id: 'name', label: 'Full Name', type: 'text', required: true },
          { id: 'email', label: 'Email', type: 'email', required: true },
          { id: 'phone', label: 'Phone', type: 'tel', required: false },
          { id: 'message', label: 'Anything else we should know?', type: 'textarea', required: false },
        ],
        submit_text: 'Send My Request',
        success_message: 'Thank you! We will be in touch within one business day.',
        notify_email: '',
      };
    case 'video-testimonial':
      return {
        headline: 'Hear From Our Clients',
        video_url: '',
        thumbnail_url: '',
        attribution_name: '',
        attribution_title: '',
        attribution_company: '',
        caption: '',
      };
    case 'framework-matrix':
      return {
        ...DEFAULT_FRAMEWORK_MATRIX,
        xAxis: { ...DEFAULT_FRAMEWORK_MATRIX.xAxis },
        yAxis: { ...DEFAULT_FRAMEWORK_MATRIX.yAxis },
        items: DEFAULT_FRAMEWORK_MATRIX.items.map((it, i) => ({ ...it, id: `m${Date.now()}${i}` })),
      };
    case 'whiteboard':
      return { title: '', background: '#FFFFFF', objects: [] };
    default:
      return {};
  }
};

// Phase 3.3 — universal framework-matrix default content (presentation-quality
// starter so an inserted matrix looks intentional, not empty).
export const DEFAULT_FRAMEWORK_MATRIX = {
  title: 'Framework Matrix',
  description: '',
  xAxis: { title: 'Effort', lowLabel: 'Low', highLabel: 'High' },
  yAxis: { title: 'Impact', lowLabel: 'Low', highLabel: 'High' },
  items: [
    { id: 'm1', title: 'Quick wins', description: 'High impact, low effort.', x: 25, y: 75, color: '' },
    { id: 'm2', title: 'Major projects', description: 'High impact, high effort.', x: 75, y: 78, color: '' },
    { id: 'm3', title: 'Fill-ins', description: 'Low impact, low effort.', x: 22, y: 25, color: '' },
    { id: 'm4', title: 'Reconsider', description: 'Low impact, high effort.', x: 74, y: 24, color: '' },
  ],
};
